import { useState, forwardRef, useImperativeHandle, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useParams } from 'react-router-dom';

import { Select } from 'antd';
import { useIsMutating } from '@tanstack/react-query';

import { StyledModal, WrapperSelect } from 'common/components';

import AddNutritionLabel from './AddNutritionLabel';
import AddSupplementLabel from './supplement-label/AddSupplementLabel';

import { useNutritionContext } from '../hook/useNutritionLabel';

import {
  SUPPLEMENT_TYPE,
  NUTRITION_TYPE,
  DRUG_FACTS_TYPE,
  PET_NUTRITION_TYPE,
} from './../../multiple-panel/constants';
import AddDrugFactsLabel from './drug-facts-label/AddDrugFactsLabel';
import PetNutritionLabel from './pet-nutrition-label/PetNutritionLabel';

import { useQueryQaFormulaData } from 'pages/qa-spec/components/qa-formula/queries';
import { getKeyQaSpecDrugFactPanels } from 'pages/qa-spec/components/qa-nutrition-data/hook';

const { Option } = Select;

const AddLabel = forwardRef(
  ({ factsPanel, visible, setVisible, onReset, typeView }, ref) => {
    const { id: productId } = useParams();

    const addNutritionRef = useRef();
    const addSupplementRef = useRef();
    const addDrugFactsRef = useRef();
    const addPetNutritionRef = useRef();

    const queryClient = useQueryClient();

    const { invalidateQaFormulaData } = useQueryQaFormulaData({
      enabled: false,
    });

    const [factType, setFactType] = useState('nutrition');

    const isMutatingAddLabel = useIsMutating({ mutationKey: ['add-label'] });

    const { setIsAddingNew, setIsUpdating, clearEditNutrientState } =
      useNutritionContext();

    const handleOk = async () => {
      if (isNutrition) addNutritionRef.current?.submitForm();

      if (isSupplement) addSupplementRef.current?.submitForm();

      if (isDrugFacts) addDrugFactsRef.current?.submitForm();

      if (isPetNutrition) addPetNutritionRef.current?.submitForm();
    };

    const initAddLabel = async (on) => {
      const panelTypeCollection = {
        [NUTRITION_TYPE]: 'nutrition',
        [SUPPLEMENT_TYPE]: 'supplement',
        [DRUG_FACTS_TYPE]: 'drugfactspanel',
        [PET_NUTRITION_TYPE]: 'petnutritionfactspanel',
      };
      const panelType =
        panelTypeCollection[factsPanel?.factsPanelType?.toLowerCase()];

      setFactType(panelType || 'nutrition');

      addNutritionRef?.current?.initForm(on);
      addSupplementRef?.current?.initForm(on);
      addDrugFactsRef?.current?.initForm(on);
      addPetNutritionRef?.current?.initForm(on);
    };

    const handleCancel = () => {
      setVisible(false);
      clearEditNutrientState();
      addNutritionRef.current?.resetForm();
      addSupplementRef.current?.resetForm();
      addDrugFactsRef.current?.resetForm();
      addPetNutritionRef.current?.resetForm();
    };

    const onChangeLabelType = (value) => {
      setFactType(value);

      if (value === 'nutrition') addNutritionRef?.current?.initForm('add');

      if (value === 'supplement') addSupplementRef?.current?.initForm('add');

      if (value === 'drugfactspanel') addDrugFactsRef?.current?.initForm('add');

      if (value === 'petnutritionfactspanel')
        addDrugFactsRef?.current?.initForm('add');
    };

    const onSubmitSuccess = () => {
      invalidateQaFormulaData({ productId });
      queryClient.invalidateQueries({
        queryKey: getKeyQaSpecDrugFactPanels(productId),
      });
      clearEditNutrientState();
      setVisible(false);
      onReset();
    };

    const onSubmitFail = () => {
      handleCancel();
    };

    useImperativeHandle(ref, () => {
      return {
        initAddLabel,
      };
    });

    const isNutrition = factType === 'nutrition';
    const isSupplement = factType === 'supplement';
    const isDrugFacts = factType === 'drugfactspanel';
    const isPetNutrition = factType === 'petnutritionfactspanel';

    const modalProps = {
      title: 'Facts Panel',
      visible: visible,
      width: 'max(1200px, 80%)',
      closable: true,
      centered: true,
      maskClosable: false,
      destroyOnClose: true,
      onCancel: handleCancel,
      wrapClassName: 'fact-panels-modal',
      onOk: handleOk,
      okButtonProps: { disabled: !factType },
      bodyStyle: factType !== 'nutrition' ? { height: '80vh' } : undefined,
    };

    return (
      <div className='nutrition-facts__add'>
        {visible && (
          <StyledModal
            {...modalProps}
            isLoading={isMutatingAddLabel}
            loadingContent={{ loadingText: 'Loading' }}
          >
            <div style={{ paddingBottom: '10px' }}>
              <span
                style={{
                  fontWeight: 'bold',
                  color: '#000',
                  paddingRight: '5px',
                }}
              >
                Type
              </span>
              <WrapperSelect
                value={factType}
                style={{ width: '20%' }}
                onChange={onChangeLabelType}
                disabled={!!factsPanel}
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
              >
                <Option value='nutrition'>Nutrition Label</Option>
                <Option value='supplement'>Supplement Label</Option>
                <Option value='drugfactspanel'>Drug Facts Label</Option>
                <Option value='petnutritionfactspanel'>
                  Pet Nutrition Label
                </Option>
              </WrapperSelect>
            </div>
            <AddNutritionLabel
              ref={addNutritionRef}
              typeView={typeView}
              visible={factType === 'nutrition'}
              productId={productId}
              factsPanel={factsPanel}
              setIsUpdating={setIsUpdating}
              setIsAddingNew={setIsAddingNew}
              onSubmitSuccess={onSubmitSuccess}
              onSubmitFail={onSubmitFail}
            />
            <AddSupplementLabel
              ref={addSupplementRef}
              typeView={typeView}
              visible={factType === 'supplement'}
              productId={productId}
              factsPanel={factsPanel}
              onSubmitSuccess={onSubmitSuccess}
              onSubmitFail={onSubmitFail}
            />
            <AddDrugFactsLabel
              ref={addDrugFactsRef}
              typeView={typeView}
              visible={factType === 'drugfactspanel'}
              productId={productId}
              factsPanel={factsPanel}
              onSubmitSuccess={onSubmitSuccess}
              onSubmitFail={onSubmitFail}
            />

            <PetNutritionLabel
              ref={addPetNutritionRef}
              typeView={typeView}
              visible={factType === 'petnutritionfactspanel'}
              productId={productId}
              factsPanel={factsPanel}
              onSubmitSuccess={onSubmitSuccess}
              onSubmitFail={onSubmitFail}
            />
          </StyledModal>
        )}
      </div>
    );
  }
);

export default AddLabel;
